import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo } from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import HelpIcon from "@mui/icons-material/Help";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";

import { error, misc, neutral, success } from "app/themes/colors";
import { CompletedAppointmentIcon } from "components/icons";
import StatusTransitionCell from "components/status-transition-cell";
import { StatusTransitionCode } from "components/status-transition-cell/status-transition-cell.component";
import {
  APPOINTMENT_STATUSES,
  APPOINTMENT_STATUS_ARRIVED,
  APPOINTMENT_STATUS_CANCELED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_CONFIRMED,
  APPOINTMENT_STATUS_NO_SHOW,
  APPOINTMENT_STATUS_RESCHEDULED,
  APPOINTMENT_STATUS_UNCONFIRMED
} from "constants/appointments";
import { NEEDS_RESCHEDULE_FEATURE } from "constants/feature-flags";
import { getIsAppointmentDateInTheFuture } from "features/scheduling/utils/scheduling.utils";
import { getAvailableStatusTransitions } from "features/scheduling/utils/status-transitions.util";
import type { Appointment } from "services/types/appointments.type";
import type { Patient } from "services/types/patients.type";

export const APPOINTMENT_STATUS_COLORS: Record<string, string> = {
  [APPOINTMENT_STATUS_UNCONFIRMED]: neutral[400],
  [APPOINTMENT_STATUS_CONFIRMED]: success[600],
  [APPOINTMENT_STATUS_CANCELED]: error[400],
  [APPOINTMENT_STATUS_NO_SHOW]: error[400],
  [APPOINTMENT_STATUS_ARRIVED]: misc.blackAsphalt,
  [APPOINTMENT_STATUS_COMPLETED]: success[600]
};

export const APPOINTMENT_STATUS_ICONS: Record<string, any> = {
  [APPOINTMENT_STATUS_UNCONFIRMED]: <HelpIcon />,
  [APPOINTMENT_STATUS_CONFIRMED]: <CheckCircleIcon />,
  [APPOINTMENT_STATUS_CANCELED]: <CancelIcon />,
  [APPOINTMENT_STATUS_NO_SHOW]: <DisabledByDefaultIcon />,
  [APPOINTMENT_STATUS_ARRIVED]: <PersonPinCircleIcon />,
  [APPOINTMENT_STATUS_RESCHEDULED]: <EditCalendarIcon />,
  [APPOINTMENT_STATUS_COMPLETED]: (
    <CompletedAppointmentIcon
      style={{ maxWidth: "14px", marginRight: "3px" }}
    />
  )
};

type AppointmentData = {
  status: number;
  startDate: string;
  needsReschedule?: boolean;
  replacement_appointment?: Partial<Appointment> | null;
  patient?: Patient;
};

type AppointmentStatusCellProps = {
  appointment: AppointmentData;
  showIconOnly?: boolean;
  overrideTooltipText?: string;
  onChange?: (status: StatusTransitionCode) => Promise<any>;
};

const DISABLED_FUTURE_STATUS_TRANSITIONS = {
  [APPOINTMENT_STATUS_NO_SHOW]:
    "Appointments that are scheduled for a future date cannot be marked as 'Patient No-Show'",
  [APPOINTMENT_STATUS_ARRIVED]:
    "Appointments that are scheduled for a future date cannot be marked as 'Patient Arrived'",
  [APPOINTMENT_STATUS_COMPLETED]:
    "Appointments that are scheduled for a future date cannot be marked as 'Complete'"
};

export default function AppointmentStatusCell({
  appointment,
  onChange,
  showIconOnly,
  overrideTooltipText
}: AppointmentStatusCellProps) {
  const flags = useFlags();
  const needsRescheduleFeature = flags[NEEDS_RESCHEDULE_FEATURE];

  const { status, startDate, needsReschedule } = appointment;
  const availableStatuses = useMemo(
    () => getAvailableStatusTransitions(status, Boolean(appointment.patient)),
    [status, appointment.patient]
  );

  if (!availableStatuses.getFromCode(status)) {
    availableStatuses.addOption(
      status,
      APPOINTMENT_STATUSES.getFromCode(status)
    );
  }

  const isAppointmentDateInTheFuture =
    getIsAppointmentDateInTheFuture(startDate);

  const isRescheduleDisplay = needsRescheduleFeature && needsReschedule;

  const hasRescheduleAppointment =
    appointment?.status === APPOINTMENT_STATUS_RESCHEDULED &&
    appointment?.replacement_appointment?.id &&
    appointment?.patient?.id;

  const navigateToReschedule = useCallback(() => {
    if (hasRescheduleAppointment) {
      const url = `/patients/${appointment?.patient?.id}/appointments/${appointment?.replacement_appointment?.id}/`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [
    appointment?.patient?.id,
    appointment?.replacement_appointment?.id,
    hasRescheduleAppointment
  ]);

  const rescheduledTooltipText = hasRescheduleAppointment
    ? "Navigate to Rescheduled Appointment"
    : undefined;

  return (
    <StatusTransitionCell
      status={status}
      statusColors={APPOINTMENT_STATUS_COLORS}
      statusIcons={APPOINTMENT_STATUS_ICONS}
      disabledStatusTransitions={
        isAppointmentDateInTheFuture
          ? { [status]: DISABLED_FUTURE_STATUS_TRANSITIONS }
          : undefined
      }
      overrideTooltipText={rescheduledTooltipText || overrideTooltipText}
      overrideCurrentText={
        isRescheduleDisplay ? "Needs to be Rescheduled" : undefined
      }
      overrideCurrentColor={isRescheduleDisplay ? misc.warningText : undefined}
      overrideCurrentIcon={
        isRescheduleDisplay ? <ManageHistoryIcon /> : undefined
      }
      allStatuses={availableStatuses}
      readonly={!onChange}
      onChange={onChange}
      onClick={navigateToReschedule}
      showIconOnly={showIconOnly}
    />
  );
}
